/**
 * Auth API Client
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const authApi = {
  /**
   * Login with Emergent OAuth session ID
   */
  async login(sessionId) {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }
    
    return response.json();
  },

  /**
   * Get current user info
   */
  async getMe() {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        return null; // Not authenticated
      }
      throw new Error('Failed to get user info');
    }
    
    return response.json();
  },

  /**
   * Logout
   */
  async logout() {
    const response = await fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    
    return response.ok;
  },

  /**
   * Get pricing tiers
   */
  async getPricingTiers() {
    const response = await fetch(`${API_URL}/api/pricing/tiers`);
    if (!response.ok) throw new Error('Failed to fetch pricing');
    return response.json();
  },

  /**
   * Create checkout session
   */
  async createCheckout(tier, billingCycle) {
    const response = await fetch(`${API_URL}/api/subscription/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        tier,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create checkout');
    }
    
    return response.json();
  },

  /**
   * Check payment status
   */
  async checkPaymentStatus(sessionId) {
    const response = await fetch(`${API_URL}/api/subscription/status/${sessionId}`, {
      credentials: 'include'
    });
    
    if (!response.ok) throw new Error('Failed to check status');
    return response.json();
  },

  /**
   * Get current subscription
   */
  async getCurrentSubscription() {
    const response = await fetch(`${API_URL}/api/subscription/current`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      if (response.status === 401) return null;
      throw new Error('Failed to get subscription');
    }
    
    return response.json();
  },

  /**
   * Cancel subscription
   */
  async cancelSubscription() {
    const response = await fetch(`${API_URL}/api/subscription/cancel`, {
      method: 'POST',
      credentials: 'include'
    });
    
    if (!response.ok) throw new Error('Failed to cancel subscription');
    return response.json();
  },

  /**
   * Get workspace members
   */
  async getWorkspaceMembers() {
    const response = await fetch(`${API_URL}/api/workspace/members`, {
      credentials: 'include'
    });
    
    if (!response.ok) throw new Error('Failed to get members');
    return response.json();
  },

  /**
   * Invite member
   */
  async inviteMember(email, role) {
    const response = await fetch(`${API_URL}/api/workspace/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, role })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to invite member');
    }
    
    return response.json();
  },

  /**
   * Remove member
   */
  async removeMember(userId) {
    const response = await fetch(`${API_URL}/api/workspace/member/${userId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    
    if (!response.ok) throw new Error('Failed to remove member');
    return response.json();
  }
};
